import React from 'react';
import { ago, type SavedTech } from './api';
import { BookmarkIcon, ChevronRight, ShareIcon } from './icons';
import { fyTrack, useImpression } from './track';

interface Props {
  items: SavedTech[];
  selected: Set<string>;
  setSelected: (s: Set<string>) => void;
  onOpen: (t: SavedTech) => void;
  onShare: () => void;
  /** Unsave from the list itself (tracker, 2026-10-29). */
  onUnsave: (t: SavedTech) => void;
}

const SavedSection: React.FC<Props> = ({ items, selected, setSelected, onOpen, onShare, onUnsave }) => {
  const ref = useImpression<HTMLElement>('saved');
  const selectable = items.filter(t => t.available);
  const n = selectable.filter(t => selected.has(t.id)).length;
  const all = selectable.length > 0 && n === selectable.length;

  const toggle = (t: SavedTech) => {
    const next = new Set(selected);
    if (next.has(t.id)) next.delete(t.id); else next.add(t.id);
    fyTrack('foryou_saved_select', { tech_id: t.id, on: next.has(t.id), count: next.size });
    setSelected(next);
  };
  const toggleAll = () => {
    const next = all ? new Set<string>() : new Set(selectable.map(t => t.id));
    fyTrack('foryou_saved_select_all', { on: !all, count: next.size });
    setSelected(next);
  };

  return (
    <section ref={ref} className="fy-sec" aria-label="Saved technologies">
      <div className="fy-sec-head">
        <h2 className="fy-h2">Saved technologies</h2>
        <div className="fy-muted">{items.length} saved</div>
      </div>
      {items.length === 0 ? (
        <div className="fy-empty">
          <div className="fy-empty-title">Nothing saved yet</div>
          <p className="fy-muted">
            Use the bookmark on any technology card to save it here. You can then share a shortlist with colleagues in
            one message.
          </p>
        </div>
      ) : (
        <>
          <div className="fy-selbar">
            <div className="fy-row">
              <input id="fy-sel-all" type="checkbox" className="fy-check" checked={all} onChange={toggleAll}
                     disabled={!selectable.length} />
              <label htmlFor="fy-sel-all" style={{ fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>Select all</label>
              <span className="fy-muted">· {n === 0 ? 'select technologies to share' : `${n} selected`}</span>
            </div>
            <button type="button" className="fy-btn fy-btn-primary" onClick={onShare} disabled={n === 0}>
              <ShareIcon /> Share
            </button>
          </div>
          <div className="fy-list">
            {items.map(t => {
              const on = selected.has(t.id);
              const cid = `fy-sv-${t.id}`;
              return (
                <div key={t.id} className={`fy-srow ${on ? 'is-on' : ''} ${t.available ? '' : 'is-gone'}`}>
                  <input id={cid} type="checkbox" className="fy-check" checked={on} disabled={!t.available}
                         onChange={() => toggle(t)} aria-label={`Select ${t.title}`} />
                  <div className="fy-srow-main">
                    <label htmlFor={cid} className="fy-srow-title">{t.title}</label>
                    <div className="fy-muted">
                      {t.available ? [t.university, `Saved ${ago(t.saved_at)}`].filter(Boolean).join(' · ')
                        : 'No longer available on Cosmos'}
                    </div>
                    {t.available && t.tags.length > 0 && (
                      <div className="fy-tags">{t.tags.slice(0, 2).map(g => <span key={g} className="fy-tag">{g}</span>)}</div>
                    )}
                  </div>
                  <button type="button" className="fy-icon-btn" aria-pressed="true"
                          aria-label={`Remove ${t.title} from saved`} title="Remove from saved"
                          onClick={() => onUnsave(t)}>
                    <BookmarkIcon filled />
                  </button>
                  {t.available && (
                    <button type="button" className="fy-icon-btn" aria-label={`Open ${t.title}`}
                            onClick={() => onOpen(t)}>
                      <ChevronRight />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </section>
  );
};

export default SavedSection;
